import React, { useState } from 'react';
import type { View } from '../../types';
import { MoreIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '../icons';
import { usePublication } from '../../lib/api/hooks/publication';

interface PublicationDetailPageProps {
  publicationId: string;
  setView: (view: View) => void;
}

const PublicationDetailPage: React.FC<PublicationDetailPageProps> = ({ publicationId, setView }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const commentsPerPage = 5;

  const { data: publicationData, isLoading, error } = usePublication(publicationId);
  const publication = publicationData?.data;

  // @ts-ignore
  const mockComments: never[] = [];

  const totalPages = Math.ceil(mockComments.length / commentsPerPage);
  const currentComments = mockComments.slice(
    (currentPage - 1) * commentsPerPage,
    currentPage * commentsPerPage
  );

  const goToPage = (pageNumber: number) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, pageNumber)));
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading publication...</p>
        </div>
      </div>
    );
  }

  if (error || !publication) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Publication not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The publication could not be found'}</p>
        <button onClick={() => setView({ page: 'Publications' })} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Publications
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => setView({ page: 'Publications' })}>Publications</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">{publication.title}</span>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                {publication.status === 'PUBLISH' ? 'Published' : 'Draft'}
              </span>
              {publication.legalFoundation && (
                <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                  Legal Foundation
                </span>
              )}
              <span className="text-xs font-semibold bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full">
                {publication.publicationCategory}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mt-2">{publication.title}</h2>
            <p className="text-sm text-gray-500 mt-2">
              Created {formatDate(publication.createdAt)} | By {publication.createdByName || publication.createdBy}
              {publication.datePublished && ` | Published ${formatDate(publication.datePublished)}`}
            </p>
          </div>
          <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
        </div>

        {/* Description */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Description</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{publication.description}</p>
        </div>

        {/* Statistics */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Views</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{publication.totalViews || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Downloads</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{publication.totalDownloads || 0}</p>
            </div>
          </div>
        </div>

        {/* File Link */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Publication File</h3>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
            <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">Publication Document</p>
            </div>
            <a
              href={publication.publicationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className=" bg-wine-100 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-wine-200 transition-colors"
            >
              View File
            </a>
          </div>
        </div>

        {/* Tags Section */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
              {publication.publicationCategory}
            </span>
            {publication.legalFoundation && (
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                Legal Foundation
              </span>
            )}
          </div>
        </div>

        {/* Comments Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-800">Comments ({mockComments.length})</h3>
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          </div>

          {mockComments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No comments yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentComments.map((comment: any) => (
                <div key={comment.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                  <img
                    src={comment.author.avatar}
                    alt={comment.author.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-sm text-gray-800">{comment.author.name}</p>
                        <p className="text-xs text-gray-500">{comment.author.email}</p>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{comment.text}</p>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === page
                          ? 'bg-[#681628] text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 pt-4 flex justify-end gap-3">
          <button
            onClick={() => setView({ page: 'Publications' })}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
          >
            Back
          </button>
          <button
            onClick={() => setView({ page: 'Publications', subPage: 'edit', id: publicationId })}
            className="px-4 py-2 bg-[#681628] text-white rounded-lg text-sm font-semibold hover:bg-[#7a1a30]"
          >
            Edit Publication
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicationDetailPage;
